import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";

import { useI18nOptional } from "@/i18n/provider";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 select-none",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        secondary: "border border-[#4B3786] text-[#4B3786] hover:bg-[#7A68B5] hover:text-white",
        warning: "bg-amber-600 hover:bg-amber-700 text-white",
        destructive: "bg-[#FF6060] text-white hover:bg-[#f05656]",
        outline:
          "border border-border bg-card text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, isLoading, children, ...props }, ref) => {
  // Se não estiver dentro do I18nProvider, usa fallback
  const i18n = useI18nOptional();
  const loadingText = i18n ? i18n.t("common.loading") : "Loading";
  const Comp = asChild ? Slot : "button";
  return (
    <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
      {isLoading ? (
        <div className="flex gap-1 items-center">
          <LoaderCircle className="animate-spin" />
          <span>{loadingText}</span>
        </div>
      ) : (
        children
      )}
    </Comp>
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };
